import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import Container from "react-bootstrap/Container";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import FilterSection from "../../../organisms/Sections/filter-controller-section/FilterSection";
import FullWidthCard, {
  CardContentItem,
} from "../../../organisms/Cards/full-width-card/FullWidthCard";
import HelperLoader from "../../../molecules/HelperLoader/HelperLoader";
import BreadCrumb from "../../../organisms/Breadcrumb/BreadCrumb";
import Headline from "../../../organisms/Headline/Headline";
import Pagination from "../../../molecules/Pagination/Pagination";
import Button from "../../../atoms/Buttons/Button/Button";
import Loader from "../../../atoms/Loader/Loader";

import { AppDispatch, RootState } from "../../../../slices/store";
import UseAuthConfig from "../../../../hooks/useAuthConfig";
import { TodosContext } from "../../../../context";
import { getHotLeads } from "../../../../slices/leads/leadManagementSlice";
import { handleDeleteCardService } from "../../../../pages/leads-management/service/LeadService";
import filterContentLeads from "../../../../utils/helpers/helperDataset/lead-management-contents/filterContentLeads.json";
import cardContent from "../../../../utils/helpers/helperDataset/lead-management-contents/cardContentLeads.json";
import useUserRole from "../../../../hooks/useUserRole";
import { UserRole } from "../../../../utils";
import useDownloadLeads from "../../../../hooks/useDownloadLeads";
import { LEAD_IDENTIFIERS } from "../../../../pages/leads-management/models/ILead";
import useDebouncedDispatch from "../../../../utils/DebouncedDispatch";
import LeadStatusDefinitions from "../../../organisms/LeadStatusDefinitions/LeadStatusDefinitions";
import { getLeadRatingText } from "../../../../utils/getLeadRatingText";

const STORAGE_KEY = "hotLeadsActivePage";

const HotLeadTemplate = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { addAlert } = useContext(TodosContext);
  const { protectedResources }: any = UseAuthConfig();
  const userRole = useUserRole();

  const leadDetails = useSelector(
    (state: RootState) => state.leadsManagement.hotLeads
  );
  const status = useSelector(
    (state: RootState) => state.leadsManagement.status
  );

  const {
    contentOne,
    contentTwo,
    contentOneShow,
    contentTwoShow,
    breadcrumbOne,
    breadcrumbTwo,
  } = filterContentLeads;

  const savedPage = Number(localStorage.getItem(STORAGE_KEY)) || 1;

  const [isLoading, setIsLoading] = useState(false);
  const [activePage, setActivePage] = useState(savedPage);
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
  const [reqRequests, setReqRequest] = useState({
    PageNumber: savedPage,
    SearchText: "",
    SortOrder: 1,
    PageSize: 12,
    OrderBy: "",
    ParameterOne: "Requested,Rejected",
    ParameterTwo: "",
    ParameterThree: "Requested,Rejected",
    url: protectedResources.apiTodoList.endpoint,
    accessToken: localStorage.getItem("AccessToken") || "",
    leadType: "HighIntent",
  });

  const { handleConfirm, handleSelectedConfirm, isLoadingDownloads } =
    useDownloadLeads(protectedResources);

  const debouncedDispatch = useDebouncedDispatch(dispatch);

  const selectedCount = selectedLeadIds.length;

  const hasLeads = !!leadDetails?.data?.length;
  const canDownload = userRole !== UserRole.Operator && hasLeads;

  const isLeadSelected = useCallback(
    (leadId: number) => selectedLeadIds.includes(leadId),
    [selectedLeadIds]
  );

  const toggleLeadSelection = useCallback((leadId: number) => {
    setSelectedLeadIds((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedLeadIds([]);
  }, []);

  const handleSetFilteredValue = useCallback((inputValue: string) => {
    setIsLoading(true);
    setActivePage(1);

    setReqRequest((prevState) => ({
      ...prevState,
      SearchText: inputValue || "",
      PageNumber: 1,
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setIsLoading(true);
    setActivePage(page);

    setReqRequest((prevState) => ({
      ...prevState,
      PageNumber: page,
    }));
  }, []);

  const handleDeleteCard = useCallback(
    (data: any) => {
      handleDeleteCardService(
        data,
        reqRequests,
        setIsLoading,
        dispatch,
        addAlert,
        false,
        navigate,
        getHotLeads,
        protectedResources
      );
    },
    [reqRequests, dispatch, addAlert, navigate, protectedResources]
  );

  const handleDownload = useCallback(() => {
    if (selectedLeadIds.length > 0) {
      handleSelectedConfirm(selectedLeadIds);
    } else {
      handleConfirm(LEAD_IDENTIFIERS.HOT_LEADS);
    }
  }, [selectedLeadIds, handleSelectedConfirm, handleConfirm]);

  useEffect(() => {
    setIsLoading(true);
    dispatch(getHotLeads(reqRequests));
  }, [dispatch]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, activePage.toString());
  }, [activePage]);

  useEffect(() => {
    setIsLoading(true);

    if (reqRequests.SearchText) {
      debouncedDispatch(getHotLeads, reqRequests);

      return () => {
        debouncedDispatch.cancel();
      };
    }

    dispatch(getHotLeads(reqRequests));
  }, [reqRequests, dispatch, debouncedDispatch]);

  useEffect(() => {
    setIsLoading(false);
  }, [leadDetails]);

  const leadCards = useMemo(
    () =>
      leadDetails?.data?.map((user: any) => {
        const updatedLead = {
          ...user,
          rating: getLeadRatingText(user.rating),
        };

        return (
          <FullWidthCard
            cardContent={cardContent as CardContentItem[]}
            key={user.id}
            data={updatedLead}
            deleteCard={handleDeleteCard}
            isSelected={isLeadSelected(user.id)}
            onSelect={() => toggleLeadSelection(user.id)}
            isLeads={true}
            canEdit={false}
          />
        );
      }),
    [leadDetails?.data, handleDeleteCard, isLeadSelected, toggleLeadSelection]
  );

  return (
    <Container fluid className="layout-bar">
      <BreadCrumb />

      <Headline
        name="High Intent Leads"
        subHeading="Smarter lead management for better conversions"
      />

      <div className="content-wrapper">
        <div className="filter-wrapper">
          <FilterSection
            contentOne={contentOne}
            contentTwo={contentTwo}
            breadcrumbOne={breadcrumbOne}
            breadcrumbTwo={breadcrumbTwo}
            handleInputText={handleSetFilteredValue}
            contentOneShow={contentOneShow}
            contentTwoShow={contentTwoShow}
          />

          <div className="download-btn-section d-flex align-items-center gap-3">
            {selectedCount > 0 && (
              <div className="clear-btn">
                <Button
                  cssClass="btn-secondary"
                  text="Clear Selection"
                  onClick={() => setSelectedLeadIds([])}
                />
              </div>
            )}

            {userRole !== UserRole.Operator &&
              (leadDetails?.data?.length ?? 0) > 0 && (
                <Button
                  cssClass="btn-primary"
                  text={
                    selectedCount > 0
                      ? `Export (${selectedCount})`
                      : "Export All"
                  }
                  icon="download"
                  onClick={() => {
                    if (selectedLeadIds.length > 0) {
                      handleSelectedConfirm(selectedLeadIds);
                    } else {
                      handleConfirm(LEAD_IDENTIFIERS.HOT_LEADS);
                    }
                  }}
                  activeColor="#ffffff"
                />
              )}
          </div>
        </div>
      </div>

      <div className="content-wrapper">
        {hasLeads ? (
          leadCards
        ) : (
          <HelperLoader
            status={status}
            totalRecords={leadDetails?.totalRecords}
          />
        )}

        <div className="pagination-section">
          <Pagination
            pageCount={leadDetails?.totalPages}
            pageNumber={activePage}
            onPageChange={handlePageChange}
            totalPages={leadDetails?.totalPages ?? 0}
            pageSize={reqRequests.PageSize}
            totalRecords={leadDetails?.totalRecords ?? 0}
            label="High Intent leads"
          />
        </div>
      </div>

      <LeadStatusDefinitions />

      {(isLoading || isLoadingDownloads) && <Loader />}
    </Container>
  );
};

export default HotLeadTemplate;
